<template>
  <v-row justify="center">
    <v-dialog v-model="showModal" persistent max-width="800px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('Update Image') }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-alert
              text
              prominent
              dismissible
              v-model="alertShow"
              type="error"
              icon="mdi-alert"
            >{{ alertText }}</v-alert>
            <v-form ref="form">
              <v-row>
                <v-col cols="12">
                  <Cropper
                    ref="cropper"
                    class="circle-stencil"
                    :src="image"
                    stencil-component="circle-stencil"
                    :stencil-props="stencilProps"
                  />
                  <div class="button-wrapper">
                    <v-btn color="primary" tile outlined class="ma-2" @click="$refs.file.click()">
                      <input
                        type="file"
                        ref="file"
                        @change="uploadImageToBrowser($event)"
                        accept="image/*"
                      />
                      {{ $t('upload') }}
                      <v-icon right dark>mdi-cloud-upload</v-icon>
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="darken-1" text @click="hide">{{ $t('close') }}</v-btn>
          <v-btn color="red" text :loading="loading" @click="removeImage">{{ $t('remove') }}</v-btn>
          <v-btn
            color="darken-1"
            :disabled="!enableCropButton"
            :loading="loading"
            text
            @click="cropImage"
          >{{ $t('update') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import axios from "axios";
import EventBus from "../../event-bus";
import { Cropper } from "vue-advanced-cropper";
import 'vue-advanced-cropper/dist/style.css';

export default {
  name: "CropImageModal",
  components: {
    Cropper,
  },
  data: () => ({
    enableCropButton: false,
    showModal: false,
    loading: false,
    alertText: null,
    alertShow: false,
    errorMessages: [],
    image: null,
    stencilProps: {
      aspectRatio: 10 / 12,
      previewClassname: "preview",
    },
  }),
  methods: {
    cleanErrorsAndTexts() {
      this.alertText = "";
      this.alertShow = false;
    },
    sendErrorAndCloseModal(error) {
      if (error.response) {
        if (
          error.response.status == 400 &&
          error.response.data.status == "error"
        ) {
          EventBus.$emit("SHOW_ERROR", error.response.data.error);
        } else {
          EventBus.$emit("SHOW_ERROR", error.response.data);
        }
      } else {
        EventBus.$emit("SHOW_ERROR", error);
      }

      this.loading = false;
      this.showModal = false;
    },
    show() {
      if (this.image != null) {
        this.enableCropButton = true;
      } else {
        this.enableCropButton = false;
      }

      this.alertShow = false;
      this.alertText = "";
      this.showModal = true;
    },
    hide() {
      this.loading = false;
      this.showModal = false;
      this.cleanErrorsAndTexts();
    },
    removeImage() {
      axios({
        url: "/api/account/user",
        headers: { "Content-Type": "application/json" },
        data: {
          avatar: { small: "", big: "" }
        },
        method: "PUT",
      })
        .then((response) => {
          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "success")
          ) {
            this.loading = false;
            this.showModal = false;
            EventBus.$emit("SHOW_ERROR", response);
          } else {
            // Update image in account site.
            this.$parent.updateAccountInfo();
            this.loading = false;
            this.showModal = false;
          }
        })
        .catch((error) => {
          this.loading = false;
          this.sendErrorAndCloseModal(error);
        });
    },
    cropImage() {
      this.loading = true;

      const { canvas } = this.$refs.cropper.getResult();

      let self = this;

      Promise.all([
        this.resizeImage(canvas.toDataURL("image/jpeg"), 200, 200),
        this.resizeImage(canvas.toDataURL("image/jpeg"), 80, 80),
      ]).then(function (avatar) {
        axios({
          url: "/api/account/user",
          headers: { "Content-Type": "application/json" },
          data: {
            avatar: {
              big: avatar[0],
              small: avatar[1],
            },
          },
          method: "PUT",
        })
          .then((response) => {
            if (
              !response.data ||
              !response.data.status ||
              !(response.data.status == "success")
            ) {
              self.loading = false;
              self.showModal = false;
              EventBus.$emit("SHOW_ERROR", response);
            } else {
              // Update image in account site.
              self.$parent.updateAccountInfo();
              self.loading = false;
              self.showModal = false;
            }
          })
          .catch((error) => {
            self.loading = false;
            self.sendErrorAndCloseModal(error);
          });
      });
    },
    uploadImageToBrowser(event) {
      // Reference to the DOM input element
      var input = event.target;
      // Ensure that you have a file before attempting to read it
      if (input.files && input.files[0]) {
        // create a new FileReader to read this image and convert to base64 format
        var reader = new FileReader();
        // Define a callback function to run, when FileReader finishes its job
        reader.onload = (e) => {
          // Note: arrow function used here, so that "this.imageData" refers to the imageData of Vue component
          // Read image as base64 and set to imageData
          this.image = e.target.result;
        };
        // Start the reader job - read file as a data url (base64 format)
        reader.readAsDataURL(input.files[0]);
        this.enableCropButton = true;
      }
    },
    resizeImage(base64Str, maxWidth = 200, maxHeight = 200) {
      return new Promise((resolve) => {
        let img = new Image();
        img.src = base64Str;
        img.onload = () => {
          let canvas = document.createElement("canvas");
          const MAX_WIDTH = maxWidth;
          const MAX_HEIGHT = maxHeight;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          canvas.width = width;
          canvas.height = height;
          let ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg"));
        };
      });
    },
  },
};
</script>

<style scoped>
.v-alert {
  overflow: hidden;
}

.vue-advanced-cropper {
  border: solid 1px #eee;
  width: 100%;
  height: 500px;
  margin: 7px;
}

input {
  display: none;
}
</style>